import React, { useEffect, useMemo, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import './v2.css';
import portraitImg from '../../../images/9B221C9F-871B-481B-9348-06401EE02F96.jpg';
import imgBaam      from '../../../images/baam_portfolio.png';
import imgEring     from '../../../images/eRing_portfolio.png';
import imgJurgis    from '../../../images/jurgis_portfolio.png';
import imgLimitless from '../../../images/limitless_portfolio.png';

/* ================================================================
   INLINE SVG HELPER
================================================================ */
const Icon = ({ children }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke="var(--acc)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

/* ================================================================
   CONTENT DATA  — edit copy/tags/projects here
================================================================ */
const SERVICES = [
  {
    icon: <Icon><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></Icon>,
    index: '01', title: 'Front-end & Mobile', delay: '0s',
    desc: 'Polished web interfaces and native-feeling mobile apps in React, Next.js & React Native — fast, accessible, and a joy to use.',
    tags: ['React', 'React Native', 'Next.js'],
  },
  {
    icon: <Icon><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></Icon>,
    index: '02', title: 'Back-end', delay: '.07s',
    desc: "APIs and data layers that stay fast under load. Node, GraphQL, and databases that won't bite you later.",
    tags: ['Node', 'GraphQL', 'Hasura', 'Python'],
  },
  {
    icon: <Icon><path d="M17.5 19a4.5 4.5 0 0 0 .5-9 6 6 0 0 0-11.6-1.5A4 4 0 0 0 6 19z" /></Icon>,
    index: '03', title: 'Cloud development', delay: '.14s',
    desc: 'Serverless functions, edge, and managed services wired together into products that just run.',
    tags: ['Supabase', 'Vercel', 'Edge', 'Netlify'],
  },
  {
    icon: <Icon><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></Icon>,
    index: '04', title: 'Cloud architecture', delay: '0s',
    desc: 'Systems designed to scale and survive. Clear boundaries, sane trade-offs, costs that stay predictable.',
    tags: ['AWS', 'GCP', 'Terraform', 'Azure'],
  },
  {
    icon: <Icon><path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" /><circle cx="12" cy="12" r="3" /></Icon>,
    index: '05', title: 'DevOps', delay: '.07s',
    desc: "CI/CD, observability, and infrastructure-as-code so shipping is boring — in the best possible way.",
    tags: ['Docker', 'GitHub Actions'],
  },
  {
    icon: <Icon><path d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8" /></Icon>,
    index: '06', title: 'Integrations', delay: '.14s',
    desc: 'Payments, auth, CMS, analytics, third-party APIs — connected cleanly so the pieces talk to each other.',
    tags: ['Strapi', 'Stripe', 'REST'],
  },
];

const PROJECTS = [
  {
    img: imgBaam,
    sector: 'Event', year: '2026', delay: '0s',
    title: 'BAAM Events — event platform',
    desc: 'Currently working as Tech Lead for BAAM Events, based in Stockholm, Sweden.',
  },
  {
    img: imgEring,
    sector: 'PropTech', year: '2026', delay: '.08s',
    title: 'eRing — smart doorbell & access',
    desc: 'Native doorbell app with CallKit UI, Firebase push notifications, Supabase backend, and DormaKaba dKey + BSL integrations for remote door access — plus a Tailwind admin dashboard with room booking.',
  },
  {
    img: imgJurgis,
    sector: 'Creative', year: '2026', delay: '.0s',
    title: 'Photo Jurgis — portfolio',
    desc: 'Photographer portfolio in React with Supabase for content management and Cloudinary for fast, optimised image delivery at any resolution.',
  },
  {
    img: imgLimitless,
    sector: 'HealthTech', year: '2025', delay: '.08s',
    title: 'Limitless — coaching platform',
    desc: 'Cross-platform coaching app and web dashboard built with Ionic, Capacitor, React and Bootstrap — onboarding, session tracking and content delivery, all backed by Supabase.',
  },
];

const STACK_GROUPS = [
  { label: 'Frontend & Mobile', delay: '0s',   items: ['React', 'Next.js', 'React Native', 'Ionic', 'Capacitor', 'TypeScript', 'Tailwind', 'Bootstrap'] },
  { label: 'Backend & Data',    delay: '.07s', items: ['Node.js', 'GraphQL', 'Hasura', 'Supabase', 'PostgreSQL'] },
  { label: 'CMS & Content',     delay: '.14s', items: ['Strapi', 'WordPress', 'Headless CMS'] },
  { label: 'Cloud & Ops',       delay: '.21s', items: ['AWS', 'Vercel', 'Firebase', 'Docker', 'GitHub', 'CI/CD', 'Google Analytics'] },
];

const TESTIMONIALS = [
  {
    quote: '"It has been a genuine pleasure working with Ole-Aleksander. Thanks to great communication and close follow-up throughout the process, we now have a product we\'re truly proud of — and we wouldn\'t hesitate for a second to recommend Brænna Consult to anyone who needs apps or IT services."',
    initials: 'OM', avatarBg: 'linear-gradient(135deg,#2a3a1a,#4a6020)', initColor: 'var(--acc)',
    name: 'Oskar Miszta', role: 'Owner & Founder, Limitless Coaching', delay: '0s',
  },
  {
    quote: '"Ole-Aleksander has an exceptional ability to solve problems well and efficiently. On top of that, he\'s incredibly approachable and easy to work with. Highly recommended!"',
    initials: 'PØ', avatarBg: 'linear-gradient(135deg,#1a2a3a,#204a60)', initColor: '#7ec8ff',
    name: 'Peter Østergaard', role: 'Instructor & Founder, Yogaskolen', delay: '.08s',
  },
  {
    quote: '"Our experience with Brænna Consult is A+. Open communication, fast delivery, and a finished product that exceeded our expectations!"',
    initials: 'MI', avatarBg: 'linear-gradient(135deg,#3a1a2a,#602040)', initColor: '#ff9ec8',
    name: 'Malin Innhaug', role: 'Principal Commercial Manager, Bonaventura Scandza', delay: '.16s',
  },
];

const PROCESS_STEPS = [
  { num: '01', title: 'Discovery',      desc: 'We map the problem, constraints, and what "done" looks like — before a line of code.', delay: '0s' },
  { num: '02', title: 'Architecture',   desc: 'I design the system and tech choices around your goals — not the other way around.',   delay: '.08s' },
  { num: '03', title: 'Build',          desc: 'Short cycles, visible progress, and demos you can react to every week.',                delay: '.16s' },
  { num: '04', title: 'Ship & iterate', desc: 'We deploy, measure, and improve. I stay on as long as you need me.',                   delay: '.24s' },
];

function validateEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

/* ================================================================
   MAIN COMPONENT
================================================================ */
export default function V2Page() {
  const [menuOpen, setMenuOpen] = useState(false);
  const mobInnerRef = useRef(null);
  const mobOuterRef = useRef(null);

  /* ---- form state ---- */
  const formRef = useRef(null);
  const [form, setForm] = useState({ user_name: '', user_email: '', message: '', company: '' });
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [submitError, setSubmitError] = useState('');

  const formErrors = useMemo(() => {
    const e = {};
    if (!form.user_name.trim()) e.user_name = 'Please enter your name.';
    if (!form.user_email.trim()) e.user_email = 'Please enter your email.';
    else if (!validateEmail(form.user_email)) e.user_email = 'Please enter a valid email address.';
    if (!form.message.trim()) e.message = 'Please add a message.';
    return e;
  }, [form]);
  const formIsValid = Object.keys(formErrors).length === 0;

  /* ---- emailjs init ---- */
  useEffect(() => {
    emailjs.init({ publicKey: 'FgLxzEO_1IGLXfUD-' });
  }, []);

  /* ---- bidirectional scroll reveal + custom cursor ---- */
  useEffect(() => {
    const revEls = Array.from(document.querySelectorAll('.rv'));
    const checkReveal = () => {
      const limit = window.innerHeight * 0.88;
      revEls.forEach(el =>
        el.classList.toggle('in', el.getBoundingClientRect().top < limit)
      );
    };
    window.addEventListener('scroll', checkReveal, { passive: true });
    checkReveal();

    const cur = document.getElementById('bc-cursor');
    let rafId = null;
    let onMove = null;

    if (cur && window.matchMedia('(hover:hover)').matches) {
      let x = window.innerWidth / 2, y = window.innerHeight / 2, tx = x, ty = y;
      onMove = e => { tx = e.clientX; ty = e.clientY; };
      window.addEventListener('mousemove', onMove);

      const loop = () => {
        x += (tx - x) * 0.18;
        y += (ty - y) * 0.18;
        cur.style.transform = `translate(${x}px,${y}px) translate(-50%,-50%)`;
        rafId = requestAnimationFrame(loop);
      };
      rafId = requestAnimationFrame(loop);

      const grow   = () => cur.classList.add('grow');
      const shrink = () => cur.classList.remove('grow');
      document.querySelectorAll('a, button').forEach(el => {
        el.addEventListener('mouseenter', grow);
        el.addEventListener('mouseleave', shrink);
      });
    }

    return () => {
      window.removeEventListener('scroll', checkReveal);
      if (onMove) window.removeEventListener('mousemove', onMove);
      if (rafId)  cancelAnimationFrame(rafId);
    };
  }, []);

  /* ---- mobile nav toggle ---- */
  const toggleMenu = () => {
    const next = !menuOpen;
    setMenuOpen(next);
    if (mobOuterRef.current && mobInnerRef.current) {
      mobOuterRef.current.style.height = next
        ? mobInnerRef.current.scrollHeight + 'px'
        : '0';
      mobOuterRef.current.classList.toggle('open', next);
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
    if (mobOuterRef.current) {
      mobOuterRef.current.style.height = '0';
      mobOuterRef.current.classList.remove('open');
    }
  };

  /* ---- form handlers ---- */
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleBlur = e => {
    setTouched(t => ({ ...t, [e.target.name]: true }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError('');
    setTouched({ user_name: true, user_email: true, message: true });
    if (!formIsValid) return;
    setSubmitStatus('sending');
    try {
      await emailjs.sendForm('service_orodo6s', 'template_cjax8vm', formRef.current);
      setSubmitStatus('sent');
      setTimeout(() => {
        setSubmitStatus('idle');
        setForm({ user_name: '', user_email: '', message: '', company: '' });
        setTouched({});
        formRef.current?.reset();
      }, 5500);
    } catch (err) {
      console.error('EmailJS error', err);
      setSubmitError('Something went wrong. Please try again or email me directly at ole-a@braenna.com');
      setSubmitStatus('idle');
    }
  };

  /* ---- field helper ---- */
  const inputStyle = hasError => ({
    background: '#0e0e12',
    border: `1px solid ${hasError ? '#e05a5a' : 'rgba(255,255,255,.12)'}`,
    borderRadius: 12, padding: '13px 14px',
    color: '#F4F4F6', fontFamily: 'inherit', fontSize: 14.5, outline: 'none',
    width: '100%',
  });

  return (
    <div className="bc-root">
      <div id="bc-cursor" />

      {/* ===== FLOATING PILL NAV ===== */}
      <div className="bc-nav-outer">
        <div className="bc-nav-pill">

          <nav className="bc-nav-left">
            <a href="#services">Services</a>
            <a href="#work">Work</a>
            <a href="#stack">Stack</a>
          </nav>

          <div className="bc-nav-top-row">
            <a href="#top" className="bc-nav-center">
              <span className="bc-logo-mark">æ</span>
              Brænna Consult
            </a>
            <button
              className={`bc-hamb${menuOpen ? ' open' : ''}`}
              onClick={toggleMenu}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <span /><span /><span />
            </button>
          </div>

          <nav className="bc-nav-right">
            <a href="#process">Process</a>
            <a href="#about">About</a>
            <a href="#contact" className="bc-nav-cta">Let's talk</a>
          </nav>

          <div ref={mobOuterRef} className="bc-mob-links-outer" style={{ height: 0 }}>
            <div ref={mobInnerRef} className="bc-mob-links-inner">
              {['services','work','stack','process','about'].map(id => (
                <a key={id} href={`#${id}`} className="bc-mob-link" onClick={closeMenu}>
                  {id.charAt(0).toUpperCase() + id.slice(1)}
                </a>
              ))}
              <a href="#contact" className="bc-mob-link bc-mob-link-accent" onClick={closeMenu}>
                Let's talk →
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* ===== HERO ===== */}
      <header id="top" className="bc-hero" style={{ position: 'relative', padding: '148px 32px 90px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -180, left: '50%', transform: 'translateX(-50%)', width: 900, height: 680, background: 'radial-gradient(ellipse at center,color-mix(in srgb,var(--acc) 16%,transparent),transparent 62%)', filter: 'blur(20px)', pointerEvents: 'none', animation: 'bcGlow 7s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)', backgroundSize: '64px 64px', maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%,#000,transparent 75%)', WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%,#000,transparent 75%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', maxWidth: 1080, margin: '0 auto', textAlign: 'center' }}>
          {/* <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '8px 16px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 999, background: 'rgba(255,255,255,.03)', fontSize: 13.5, fontWeight: 500, color: '#cfcfd5', marginBottom: 34, opacity: 0, animation: 'bcUp .7s cubic-bezier(.22,.61,.36,1) .05s forwards' }}>
            <span style={{ position: 'relative', width: 8, height: 8 }}>
              <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--acc)', animation: 'bcPulse 2.2s ease infinite' }} />
              <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--acc)' }} />
            </span>
            Available for new projects · Q4 2026
          </div> */}

          <h1 className="bc-h1" style={{ fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 800, fontSize: 'clamp(44px,7.2vw,104px)', lineHeight: .96, letterSpacing: '-.038em', margin: 0, opacity: 0, animation: 'bcUp .8s cubic-bezier(.22,.61,.36,1) .12s forwards' }}>
            Software that ships.<br />Systems that <span className="bc-accent">scale.</span>
          </h1>

          <p style={{ fontSize: 'clamp(17px,1.5vw,21px)', lineHeight: 1.55, color: '#a4a4ac', maxWidth: 620, margin: '32px auto 0', opacity: 0, animation: 'bcUp .8s cubic-bezier(.22,.61,.36,1) .22s forwards' }}>
            Full-stack, mobile &amp; cloud consulting for teams that need it done right — web apps, native apps, APIs, infrastructure. One partner, end to end.
          </p>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 40, opacity: 0, animation: 'bcUp .8s cubic-bezier(.22,.61,.36,1) .3s forwards' }}>
            <a href="#contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 15.5, fontWeight: 600, background: 'var(--acc)', color: '#0B0B0E', padding: '16px 28px', borderRadius: 999 }}>Start a project →</a>
            <a href="#work"    style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 15.5, fontWeight: 600, color: '#F4F4F6', padding: '16px 28px', borderRadius: 999, border: '1px solid rgba(255,255,255,.16)', background: 'rgba(255,255,255,.02)' }}>See my work</a>
          </div>

          <div className="bc-stat-row" style={{ display: 'flex', gap: 40, justifyContent: 'center', marginTop: 64, opacity: 0, animation: 'bcUp .8s cubic-bezier(.22,.61,.36,1) .4s forwards' }}>
            {[['3+', 'Years building'], ['15+', 'Projects shipped'], ['∞', 'Coffee deployed']].map(([num, label], i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="bc-stat-div" style={{ width: 1, background: 'rgba(255,255,255,.1)' }} />}
                <div>
                  <div style={{ fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 700, fontSize: 34, letterSpacing: '-.02em' }}>
                    {num.replace('+', '')}{num.includes('+') && <span className="bc-accent">+</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#86868e', marginTop: 2 }}>{label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </header>

      {/* ===== CLIENTS MARQUEE ===== */}
      <section style={{ padding: '30px 0 64px', borderTop: '1px solid rgba(255,255,255,.06)', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: '#6b6b73', marginBottom: 30 }}>
          Trusted by teams across the Nordics
        </div>
        <div style={{ position: 'relative', overflow: 'hidden', maskImage: 'linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)', WebkitMaskImage: 'linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)' }}>
          {/* Two identical inner divs — track animates -50% so the seam between them is seamless */}
          <div style={{ display: 'flex', width: 'max-content', animation: 'bcMarq 36s linear infinite', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 27, color: 'rgba(244,244,246,.34)', letterSpacing: '-.01em' }}>
            {[0, 1].map(copy => (
              <div key={copy} style={{ display: 'flex', alignItems: 'center', gap: 64, paddingRight: 64 }}>
                {['Simple','LAMINOFLY','MyLuck','Verktøypartner','Bonaventura Scandza','Limitless Coaching','Photo Jurgis','gym_fluence','Intense','BAAM Events'].map(n => (
                  <span key={n}>{n}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" style={{ maxWidth: 1200, margin: '0 auto', padding: '110px 32px 40px' }}>
        <div className="bc-section-header">
          <div>
            <p className="rv bc-eyebrow">(01) — Services</p>
            <h2 className="rv bc-h2" style={{ '--d': '.08s', maxWidth: 680 }}>Everything between an idea and production.</h2>
          </div>
          <p className="rv bc-sub" style={{ '--d': '.16s', maxWidth: 340 }}>Web, mobile, or the whole pipeline. I plug into your team and own the parts that need owning.</p>
        </div>
        <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
          {SERVICES.map(s => (
            <div key={s.index} className="rv svc-card" style={{ '--d': s.delay }}>
              <div className="svc-inner">
                <div className="bc-icon-tile">{s.icon}</div>
                <p className="bc-card-index">{s.index}</p>
                <h3 className="bc-h3">{s.title}</h3>
                <p className="bc-card-body">{s.desc}</p>
                <div className="bc-chip-row mt-2">
                  {s.tags.map(t => <span key={t} className="bc-chip">{t}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== WORK / PROJECTS ===== */}
      <section id="work" style={{ maxWidth: 1200, margin: '0 auto', padding: '110px 32px 40px' }}>
        <div className="bc-section-header">
          <div>
            <p className="rv bc-eyebrow">(02) — Selected work</p>
            <h2 className="rv bc-h2" style={{ '--d': '.08s', maxWidth: 680 }}>A few things I've shipped recently.</h2>
          </div>
          <a href="#contact" className="rv bc-link-accent" style={{ '--d': '.16s' }}>Start yours →</a>
        </div>
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {PROJECTS.map((p, i) => (
            <a key={i} href="#work" className="rv proj-item" style={{ '--d': p.delay }}>
              <div className="proj-card">
                <div className="proj-visual">
                  <div className="proj-visual-bg">
                    <img src={p.img} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                  </div>
                </div>
                <div style={{ padding: '26px 28px 28px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14, fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#8a8a92' }}>
                    <span className="bc-chip" style={{ borderRadius: 6, padding: '3px 8px' }}>{p.sector}</span>
                    <span className="bc-chip" style={{ borderRadius: 6, padding: '3px 8px' }}>{p.year}</span>
                  </div>
                  <h3 style={{ fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 700, fontSize: 24, letterSpacing: '-.02em', margin: '0 0 8px' }}>{p.title}</h3>
                  <p style={{ fontSize: 15, lineHeight: 1.6, color: '#9a9aa2', margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.desc}</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ===== STACK / COMPETENCE ===== */}
      <section id="stack" style={{ position: 'relative', marginTop: 70, padding: '96px 0', background: 'rgba(255,255,255,.018)', borderTop: '1px solid rgba(255,255,255,.06)', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: 54 }}>
            <p className="rv bc-eyebrow">(03) — Competence</p>
            <h2 className="rv bc-h2" style={{ '--d': '.08s', margin: '0 auto', maxWidth: 760 }}>The tools I reach for.</h2>
            <p className="rv bc-sub" style={{ '--d': '.16s', maxWidth: 520, margin: '20px auto 0' }}>Years of building production software and apps — here's what's in the kit, grouped by where it lives.</p>
          </div>
          <div className="grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
            {STACK_GROUPS.map(g => (
              <div key={g.label} className="rv" style={{ '--d': g.delay, border: '1px solid rgba(255,255,255,.09)', borderRadius: 20, padding: 26, background: '#0e0e12' }}>
                <p className="bc-stack-group-label">{g.label}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {g.items.map(item => <span key={item} className="bc-stack-chip">{item}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section id="process" style={{ maxWidth: 1200, margin: '0 auto', padding: '110px 32px 40px' }}>
        <div style={{ marginBottom: 60 }}>
          <p className="rv bc-eyebrow">(04) — How I work</p>
          <h2 className="rv bc-h2" style={{ '--d': '.08s', maxWidth: 680 }}>A simple loop that keeps you in the room.</h2>
        </div>
        <div className="grid-proc" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, borderTop: '1px solid rgba(255,255,255,.1)' }}>
          {PROCESS_STEPS.map((step, i) => (
            <div key={step.num} className="rv" style={{ '--d': step.delay, padding: i === 0 ? '30px 24px 30px 0' : i === PROCESS_STEPS.length - 1 ? '30px 0 30px 24px' : '30px 24px', borderRight: i < PROCESS_STEPS.length - 1 ? '1px solid rgba(255,255,255,.08)' : 'none' }}>
              <p className="bc-proc-num">{step.num}</p>
              <h3 className="bc-proc-title">{step.title}</h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: '#9a9aa2', margin: 0 }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section id="words" style={{ maxWidth: 1200, margin: '0 auto', padding: '110px 32px 40px' }}>
        <div style={{ marginBottom: 54 }}>
          <p className="rv bc-eyebrow">(05) — Kind words</p>
          <h2 className="rv bc-h2" style={{ '--d': '.08s', maxWidth: 680 }}>What clients say afterwards.</h2>
        </div>
        <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="rv test-card" style={{ '--d': t.delay }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="var(--acc)" style={{ opacity: .85, marginBottom: 18 }}>
                <path d="M10 7L7 13v4h4v-4H8.5L10.8 8zM18 7l-3 6v4h4v-4h-2.5L18.8 8z" />
              </svg>
              <p style={{ fontSize: 17, lineHeight: 1.55, color: '#dcdce0', margin: '0 0 26px', flex: 1 }}>{t.quote}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: t.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 700, fontSize: 16, color: t.initColor, flexShrink: 0 }}>{t.initials}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                  <div style={{ fontSize: 13, color: '#86868e' }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" style={{ maxWidth: 1200, margin: '0 auto', padding: '110px 32px 40px' }}>
        <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: '.85fr 1.15fr', gap: 60, alignItems: 'center' }}>
          {/* Portrait */}
          <div className="rv" style={{ position: 'relative' }}>
            <div className="about-img" style={{ aspectRatio: '4/5', borderRadius: 26, border: '1px solid rgba(255,255,255,.09)', overflow: 'hidden', position: 'relative' }}>
              <img
                src={portraitImg}
                alt="Ole-A Brænna"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
              />
            </div>
            {/* Stacked credential cards — outer div rotates, inner div floats (can't combine in one element) */}
            <div style={{ position: 'absolute', bottom: 22, right: -20, zIndex: 10 }}>
              <div style={{ position: 'relative' }}>
                {/* Cloud Foundations — back, most rotated */}
                <div style={{ position: 'absolute', bottom: 0, right: 0, zIndex: 1, transform: 'rotate(-12deg)', transformOrigin: 'bottom right' }}>
                  <div style={{ width: 148, background: '#0d1a0a', border: '1px solid color-mix(in srgb,var(--acc) 35%,transparent)', color: 'var(--acc)', borderRadius: 14, padding: '14px 18px', animation: 'bcFloat 5.2s ease-in-out 3s infinite' }}>
                    <div style={{ fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 800, fontSize: 20, lineHeight: 1 }}>A</div>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.02em' }}>Cloud · Noroff</div>
                  </div>
                </div>
                {/* Front-end — middle */}
                <div style={{ position: 'absolute', bottom: 0, right: 0, zIndex: 2, transform: 'rotate(-6deg)', transformOrigin: 'bottom right' }}>
                  <div style={{ width: 148, background: 'color-mix(in srgb,var(--acc) 58%,#0B0B0E)', color: '#0B0B0E', borderRadius: 14, padding: '14px 18px', animation: 'bcFloat 4.8s ease-in-out 1.5s infinite' }}>
                    <div style={{ fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 800, fontSize: 20, lineHeight: 1 }}>A</div>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.02em' }}>Front-end · Noroff</div>
                  </div>
                </div>
                {/* Back-end — front */}
                <div style={{ position: 'relative', zIndex: 3 }}>
                  <div style={{ width: 148, background: 'var(--acc)', color: '#0B0B0E', borderRadius: 14, padding: '14px 18px', animation: 'bcFloat 4.5s ease-in-out 0s infinite' }}>
                    <div style={{ fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 800, fontSize: 20, lineHeight: 1 }}>A</div>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.02em' }}>Back-end · Noroff</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Text */}
          <div>
            <p className="rv bc-eyebrow">(06) — About</p>
            <h2 className="rv" style={{ '--d': '.08s', fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 700, fontSize: 'clamp(28px,3.6vw,46px)', lineHeight: 1.06, letterSpacing: '-.03em', margin: '0 0 24px' }}>
              Hi, I'm Ole-Aleksander — the consultant behind Brænna Consult.
            </h2>
            <p className="rv bc-body" style={{ '--d': '.14s', margin: '0 0 18px' }}>
              I'm an independent IT consultant based in Norway, helping teams design and build software that lasts. I work across the full stack — web apps, mobile apps, APIs, and the cloud beneath it all — comfortable owning a feature, a service, or an entire product end to end.
            </p>
            <p className="rv bc-body" style={{ '--d': '.2s', margin: '0 0 28px' }}>
              I care about clear thinking, calm delivery, and code that the next person can actually read. When I'm not shipping, I'm probably reading docs I didn't need to read yet.
            </p>
            <div className="rv" style={{ '--d': '.26s', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { title: 'Back-end Development',  sub: 'Noroff · Final grade A' },
                { title: 'Front-end Development', sub: 'Noroff · Final grade A' },
                { title: 'Cloud Foundations',     sub: 'Noroff · Final grade A' },
              ].map(c => (
                <div key={c.title} className="bc-cred">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--acc)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10L12 5 2 10l10 5 10-5zM6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
                  </svg>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{c.title}</div>
                    <div style={{ fontSize: 12.5, color: '#86868e' }}>{c.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" style={{ position: 'relative', marginTop: 70, padding: '100px 0', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ position: 'absolute', bottom: -220, left: '50%', transform: 'translateX(-50%)', width: 1000, height: 600, background: 'radial-gradient(ellipse at center,color-mix(in srgb,var(--acc) 13%,transparent),transparent 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: 1080, margin: '0 auto', padding: '0 32px' }}>
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'start' }}>

            {/* Contact info */}
            <div>
              <p className="rv bc-eyebrow">(07) — Contact</p>
              <h2 className="rv" style={{ '--d': '.08s', fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 800, fontSize: 'clamp(34px,4.6vw,60px)', lineHeight: 1, letterSpacing: '-.035em', margin: '0 0 22px' }}>
                Let's build something solid.
              </h2>
              <p className="rv bc-body" style={{ '--d': '.16s', margin: '0 0 36px', maxWidth: 380 }}>
                Got a project, a problem, or a maybe? Tell me a little about it and I'll get back to you within a day.
              </p>
              <div className="rv" style={{ '--d': '.22s', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <a href="mailto:ole-a@braenna.com" className="bc-contact-row">
                  <span className="bc-contact-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--acc)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 12, color: '#86868e' }}>Email</span>
                    <span style={{ fontSize: 15.5, fontWeight: 600 }}>ole-a@braenna.com</span>
                  </span>
                </a>
                <div className="bc-contact-row">
                  <span className="bc-contact-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--acc)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 12, color: '#86868e' }}>Phone</span>
                    <span style={{ fontSize: 15.5, fontWeight: 600, color: '#9a9aa2' }}>+47 406 28 163</span>
                  </span>
                </div>
                <a href="https://www.linkedin.com/in/ole-aleksander-br%C3%A6nna-210666252/" target="_blank" rel="noreferrer" className="bc-contact-row">
                  <span className="bc-contact-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--acc)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2" /><path d="M7 10v6M7 7v.01M11 16v-3a2 2 0 0 1 4 0v3M11 13v3" /></svg>
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 12, color: '#86868e' }}>LinkedIn</span>
                    <span style={{ fontSize: 15.5, fontWeight: 600, color: '#9a9aa2' }}>/in/braenna</span>
                  </span>
                </a>
              </div>
            </div>

            {/* Form */}
            <form id="bc-form" ref={formRef} className="rv" style={{ '--d': '.14s', border: '1px solid rgba(255,255,255,.1)', background: 'rgba(255,255,255,.025)', borderRadius: 24, padding: 32, display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={handleSubmit} noValidate>
              <div className="form-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#9a9aa2', fontWeight: 500 }}>
                  <span>Name</span>
                  <input name="user_name" required value={form.user_name} onChange={handleChange} onBlur={handleBlur} autoComplete="name" style={inputStyle(touched.user_name && formErrors.user_name)} />
                  {touched.user_name && formErrors.user_name && <span style={{ fontSize: 12, color: '#e05a5a' }}>{formErrors.user_name}</span>}
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#9a9aa2', fontWeight: 500 }}>
                  <span>Email</span>
                  <input name="user_email" type="email" required value={form.user_email} onChange={handleChange} onBlur={handleBlur} autoComplete="email" style={inputStyle(touched.user_email && formErrors.user_email)} />
                  {touched.user_email && formErrors.user_email && <span style={{ fontSize: 12, color: '#e05a5a' }}>{formErrors.user_email}</span>}
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#9a9aa2', fontWeight: 500 }}>
                <span>Company <span style={{ color: '#5a5a62' }}>(optional)</span></span>
                <input name="company" value={form.company} onChange={handleChange} style={inputStyle(false)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#9a9aa2', fontWeight: 500 }}>
                <span>What can I help with?</span>
                <textarea name="message" rows={4} required value={form.message} onChange={handleChange} onBlur={handleBlur} style={{ ...inputStyle(touched.message && formErrors.message), resize: 'vertical' }} />
                {touched.message && formErrors.message && <span style={{ fontSize: 12, color: '#e05a5a' }}>{formErrors.message}</span>}
              </label>
              {submitError && (
                <p role="alert" style={{ fontSize: 13, color: '#e05a5a', margin: 0 }}>{submitError}</p>
              )}
              <button type="submit" disabled={submitStatus !== 'idle'} style={{ marginTop: 4, border: 'none', borderRadius: 12, padding: 15, fontFamily: 'inherit', fontSize: 15.5, fontWeight: 700, transition: 'background .3s, color .3s, opacity .25s', background: submitStatus === 'sent' ? '#2a4a1a' : 'var(--acc)', color: submitStatus === 'sent' ? 'var(--acc)' : '#0B0B0E', cursor: submitStatus === 'idle' ? 'pointer' : submitStatus === 'sending' ? 'wait' : 'default', opacity: submitStatus === 'sending' ? 0.7 : 1 }}>
                {submitStatus === 'sending' ? 'Sending…' : submitStatus === 'sent' ? 'Message sent ✓' : 'Send message →'}
              </button>
            </form>

          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,.07)', padding: '56px 32px 40px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 30, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, fontFamily: "'Schibsted Grotesk',sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-.02em' }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--acc)', color: '#0B0B0E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>æ</span>
            Brænna Consult
          </div>
          <div className="bc-footer-links" style={{ display: 'flex', gap: 28, fontSize: 14, color: '#9a9aa2', fontWeight: 500 }}>
            <a href="#services">Services</a>
            <a href="#work">Work</a>
            <a href="#stack">Stack</a>
            <a href="#contact">Let's talk</a>
          </div>
          <div style={{ fontSize: 13, color: '#6b6b73', fontFamily: "'JetBrains Mono',monospace" }}>
            © 2026 Brænna Consult · Built in Norway
          </div>
        </div>
      </footer>

    </div>
  );
}
